import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Nav, Badge } from 'react-bootstrap';
import authService from '../services/authService';
import businessService from '../services/businessService';
import aiService from '../services/aiService';
import analyticsService from '../services/analyticsService';
import targetMarketService from '../services/targetMarketService';
import BrandNavBar from '../components/BrandNavBar';
import { colors, gradients } from '../brand-colors';

function Dashboard() {
  const navigate = useNavigate();
  const { module } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(module || 'foundation');
  const [businessBasicsCompleted, setBusinessBasicsCompleted] = useState(false);
  const [coreValuesCompleted, setCoreValuesCompleted] = useState(false);
  const [missionCompleted, setMissionCompleted] = useState(false);
  const [visionCompleted, setVisionCompleted] = useState(false);
  const [targetMarketCompleted, setTargetMarketCompleted] = useState(false);
  const [customerJourneyCompleted, setCustomerJourneyCompleted] = useState(false);
  const [resultsAlignmentCompleted, setResultsAlignmentCompleted] = useState(false);
  const [capacityAssessmentCompleted, setCapacityAssessmentCompleted] = useState(false);
  // exitStrategyCompleted removed - old feature replaced by UpdatedExitStrategy in marketing module
  const [coreProductPricingCompleted, setCoreProductPricingCompleted] = useState(false);
  const [referralSourceCompleted, setReferralSourceCompleted] = useState(false);
  const [customerExperienceCompleted, setCustomerExperienceCompleted] = useState(false);
  const [competitiveAnalysisCompleted, setCompetitiveAnalysisCompleted] = useState(false);
  const [featuresListCompleted, setFeaturesListCompleted] = useState(false);
  const [roadmapCompleted, setRoadmapCompleted] = useState(false);
  const [productCatalogCompleted, setProductCatalogCompleted] = useState(false);
  const [upgradePathsCompleted, setUpgradePathsCompleted] = useState(false);
  const [uspCompleted, setUspCompleted] = useState(false);
  const [irresistibleOfferCompleted, setIrresistibleOfferCompleted] = useState(false);
  const [brandArchetypeCompleted, setBrandArchetypeCompleted] = useState(false);
  const [colorPaletteCompleted, setColorPaletteCompleted] = useState(false);
  const [typographyCompleted, setTypographyCompleted] = useState(false);
  const [logoDirectionCompleted, setLogoDirectionCompleted] = useState(false);
  const [iconographyCompleted, setIconographyCompleted] = useState(false);
  const [brandStyleCompleted, setBrandStyleCompleted] = useState(false);
  const [brandTextureCompleted, setBrandTextureCompleted] = useState(false);
  const [brandVoiceAnalysisCompleted, setBrandVoiceAnalysisCompleted] = useState(false);
  const [moodBoardCompleted, setMoodBoardCompleted] = useState(false);
  const [marketingAssessmentCompleted, setMarketingAssessmentCompleted] = useState(false);
  const [marketingPrioritiesCompleted, setMarketingPrioritiesCompleted] = useState(false);
  const [clientRetentionCompleted, setClientRetentionCompleted] = useState(false);
  const [viralReferralCompleted, setViralReferralCompleted] = useState(false);
  const [plannedPromotionsCompleted, setPlannedPromotionsCompleted] = useState(false);
  const [exitPathConfigured, setExitPathConfigured] = useState(false);
  const [leadMagnetCompleted, setLeadMagnetCompleted] = useState(false);
  const [salesPageCompleted, setSalesPageCompleted] = useState(false);
  const [foundationModuleComplete, setFoundationModuleComplete] = useState(false);
  const [onboardingPath, setOnboardingPath] = useState(null);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Update active module when URL param changes
    if (module) {
      setActiveModule(module);
    }
  }, [module]);

  const checkAuth = async () => {
    const result = await authService.checkAuth();
    if (result.authenticated) {
      setUser(result.user);

      // Check if user has seen welcome screen
      const welcomeResult = await businessService.getField('welcome_completed');
      // Note: API returns 'true'/'false' strings for boolean fields
      if (!welcomeResult.success || welcomeResult.data.field_value !== 'true') {
        // Redirect to welcome page for first-time users
        navigate('/welcome');
        return;
      }

      // Get onboarding path (quick_start, quick_start_exploring, or guided_journey)
      // Default to guided_journey if not set (for users who signed up before this was tracked)
      const pathResult = await businessService.getField('onboarding_path');
      if (pathResult.success && pathResult.data.field_value) {
        setOnboardingPath(pathResult.data.field_value);

        // If quick-start user who hasn't switched to exploring mode,
        // redirect them to the quick-start dashboard
        // (quick_start_exploring users stay on main dashboard)
        if (pathResult.data.field_value === 'quick_start') {
          navigate('/quick-start-dashboard');
          return;
        }
      } else {
        // Legacy users default to guided_journey (they didn't have quick-start option)
        setOnboardingPath('guided_journey');
      }

      // Get all dashboard status in ONE API call (much faster!)
      const dashboardStatusResult = await fetch('/api/dashboard/status', {
        method: 'GET',
        credentials: 'include'
      });

      if (dashboardStatusResult.ok) {
        const status = await dashboardStatusResult.json();

        // Update all state from the single response
        setBusinessBasicsCompleted(status.businessBasicsCompleted);
        setCoreValuesCompleted(status.coreValuesCompleted);
        setMissionCompleted(status.missionCompleted);
        setVisionCompleted(status.visionCompleted);
        setTargetMarketCompleted(status.targetMarketCompleted);
        setCustomerJourneyCompleted(status.customerJourneyCompleted);
        setResultsAlignmentCompleted(status.resultsAlignmentCompleted);
        setCapacityAssessmentCompleted(status.capacityAssessmentCompleted);
        // exitStrategyCompleted removed - old feature replaced by UpdatedExitStrategy
        setCoreProductPricingCompleted(status.coreProductPricingCompleted);
        setReferralSourceCompleted(status.referralSourceCompleted);
        setCustomerExperienceCompleted(status.customerExperienceCompleted);
        setCompetitiveAnalysisCompleted(status.competitiveAnalysisCompleted);
        setFeaturesListCompleted(status.featuresListCompleted);
        setRoadmapCompleted(status.roadmapCompleted);
        setProductCatalogCompleted(status.productCatalogCompleted);
        setUpgradePathsCompleted(status.upgradePathsCompleted);
        setUspCompleted(status.uspCompleted);
        setIrresistibleOfferCompleted(status.irresistibleOfferCompleted);
        setBrandArchetypeCompleted(status.brandArchetypeCompleted);
        setColorPaletteCompleted(status.colorPaletteCompleted);
        setTypographyCompleted(status.typographyCompleted);
        setLogoDirectionCompleted(status.logoDirectionCompleted);
        setIconographyCompleted(status.iconographyCompleted);
        setBrandStyleCompleted(status.brandStyleCompleted);
        setBrandTextureCompleted(status.brandTextureCompleted);
        setBrandVoiceAnalysisCompleted(status.brandVoiceAnalysisCompleted);
        setMoodBoardCompleted(status.moodBoardCompleted);
        setMarketingAssessmentCompleted(status.marketingAssessmentCompleted);
        setMarketingPrioritiesCompleted(status.marketingPrioritiesCompleted);
        setClientRetentionCompleted(status.clientRetentionCompleted);
        setViralReferralCompleted(status.viralReferralCompleted);
        setPlannedPromotionsCompleted(status.plannedPromotionsCompleted);
        setExitPathConfigured(status.exitPathConfigured);
        setLeadMagnetCompleted(status.leadMagnetCompleted);
        setSalesPageCompleted(status.salesPageCompleted);

        // Compute and set if Foundation module is complete (all required sections done)
        // Note: Exit Strategy is optional and not required to unlock Module 2
        setFoundationModuleComplete(
          status.businessBasicsCompleted &&
          status.coreValuesCompleted &&
          status.missionCompleted &&
          status.visionCompleted &&
          status.targetMarketCompleted &&
          status.customerJourneyCompleted &&
          status.resultsAlignmentCompleted &&
          status.capacityAssessmentCompleted
        );

        // Auto-redirect to appropriate module based on completion status
        // Only redirect if on /dashboard (no module specified)
        if (!module) {
          // Foundation module is complete when all required sections are done (Exit Strategy is optional)
          if (status.businessBasicsCompleted &&
              status.coreValuesCompleted &&
              status.missionCompleted &&
              status.visionCompleted &&
              status.targetMarketCompleted &&
              status.customerJourneyCompleted &&
              status.resultsAlignmentCompleted &&
              status.capacityAssessmentCompleted) {
            // Module 1 is done, check Module 2 (Business Model)
            const module2Complete = status.competitiveAnalysisCompleted; // For now, use competitive analysis as proxy

            if (!module2Complete) {
              // Redirect to Business Model module
              navigate('/dashboard/business-model', { replace: true });
            } else {
              // Redirect to next module (when we have more modules)
              navigate('/dashboard/business-model', { replace: true });
            }
          } else {
            // Module 1 not complete, redirect to foundation
            navigate('/dashboard/foundation', { replace: true });
          }
        }
      }
    } else {
      navigate('/login');
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    analyticsService.auth.logout();
    await authService.logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <p>Loading...</p>
      </Container>
    );
  }

  return (
    <>
      <BrandNavBar
        appName="Profit Map"
        screenName="Dashboard"
        userEmail={user?.email}
        onLogout={handleLogout}
        showQuickStart={onboardingPath === 'guided_journey'}
        showBackToQuickStart={onboardingPath === 'quick_start_exploring'}
      />

      <Container className="py-4">

      {/* Quick-Start Exploring Mode Banner */}
      {onboardingPath === 'quick_start_exploring' && (
        <Row className="mb-3">
          <Col md={12}>
            <Alert style={{ backgroundColor: '#cca557', borderColor: '#ba934f', color: '#0a0a0a' }}>
              <strong>Exploring Guided Journey Mode</strong>
              <p className="mb-0 mt-1">
                You've completed the Quick-Start interview. You can now explore the detailed modules
                and <strong>edit any information</strong> you entered during quick-start. Click on any
                step marked as "Completed" to review or update your data.
              </p>
            </Alert>
          </Col>
        </Row>
      )}

      {/* Module Tabs */}
      <Row className="mb-4">
        <Col>
          <Nav variant="tabs" activeKey={activeModule} onSelect={(k) => navigate(`/dashboard/${k}`)}>
            <Nav.Item>
              <Nav.Link eventKey="foundation">Foundation</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="business-model">Business Model</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="marketing-strategy">Marketing Strategy</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="battle-plan">Strategic Intelligence</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="branding">Branding</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="assets-creation">Assets Creation</Nav.Link>
            </Nav.Item>
            {user?.command_center_tier && (
              <Nav.Item>
                <Nav.Link onClick={() => navigate('/command-center')}>Command Center</Nav.Link>
              </Nav.Item>
            )}
          </Nav>
        </Col>
      </Row>

      {/* Foundation Module Cards */}
      {activeModule === 'foundation' && (
        <>
          <Row className="mb-3">
            <Col>
              <h3>Foundation Module</h3>
              <p className="text-muted">
                Build your marketing foundation by defining your business basics and core values.
              </p>
            </Col>
          </Row>
          <Row>
        <Col md={6} lg={4} className="mb-3">
          <Card className={businessBasicsCompleted ? 'border-success' : ''}>
            <Card.Body>
              <Card.Title>Step 1: Business Basics</Card.Title>
              <Card.Text>
                Define your business name, industry, location, and core offerings.
              </Card.Text>
              {businessBasicsCompleted && (
                <Alert className="mb-2 py-1 small alert-brand-completed">
                  Completed ✓
                </Alert>
              )}
              <Button
                onClick={() => navigate('/business-basics')}
                className={`w-100 mt-2 ${businessBasicsCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
              >
                {businessBasicsCompleted ? 'Edit' : 'Get Started'}
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4} className="mb-3">
          <Card className={coreValuesCompleted ? 'border-success' : ''}>
            <Card.Body>
              <Card.Title>Step 2: Core Values</Card.Title>
              <Card.Text>
                Discover your authentic core values through AI-guided conversation.
              </Card.Text>
              {coreValuesCompleted && (
                <Alert className="mb-2 py-1 small alert-brand-completed">
                  Completed ✓
                </Alert>
              )}
              <Button
                onClick={() => navigate('/core-values')}
                className={`w-100 mt-2 ${coreValuesCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                disabled={!businessBasicsCompleted}
              >
                {coreValuesCompleted ? 'View/Edit' : businessBasicsCompleted ? 'Get Started' : 'Complete Business Basics First'}
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4} className="mb-3">
          <Card className={targetMarketCompleted ? 'border-success' : ''}>
            <Card.Body>
              <Card.Title>Step 3: Target Market Discovery</Card.Title>
              <Card.Text>
                AI-powered analysis to identify and select your primary target market niche.
              </Card.Text>
              {targetMarketCompleted && (
                <Alert className="mb-2 py-1 small alert-brand-completed">
                  Completed ✓
                </Alert>
              )}
              <Button
                onClick={() => navigate('/target-market-discovery')}
                className={`w-100 mt-2 ${targetMarketCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                disabled={!businessBasicsCompleted}
              >
                {targetMarketCompleted ? 'View/Edit' : businessBasicsCompleted ? 'Get Started' : 'Complete Business Basics First'}
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4} className="mb-3">
          <Card className={missionCompleted ? 'border-success' : 'border-secondary'}>
            <Card.Body>
              <Card.Title>
                Mission Statement
                <span className="badge bg-secondary ms-2" style={{fontSize: '0.6em'}}>Optional</span>
              </Card.Title>
              <Card.Text>
                Craft a compelling mission statement that articulates your business purpose.
              </Card.Text>
              {missionCompleted && (
                <Alert className="mb-2 py-1 small alert-brand-completed">
                  Completed ✓
                </Alert>
              )}
              <Button
                onClick={() => navigate('/mission')}
                className={`w-100 mt-2 ${missionCompleted ? 'btn-brand-completed' : 'btn-brand-optional'}`}
                disabled={!businessBasicsCompleted}
              >
                {missionCompleted ? 'View/Edit' : businessBasicsCompleted ? 'Create Mission' : 'Complete Business Basics First'}
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4} className="mb-3">
          <Card className={visionCompleted ? 'border-success' : 'border-secondary'}>
            <Card.Body>
              <Card.Title>
                Vision Statement
                <span className="badge bg-secondary ms-2" style={{fontSize: '0.6em'}}>Optional</span>
              </Card.Title>
              <Card.Text>
                Create an inspiring vision that paints the future you're working to build.
              </Card.Text>
              {visionCompleted && (
                <Alert className="mb-2 py-1 small alert-brand-completed">
                  Completed ✓
                </Alert>
              )}
              <Button
                onClick={() => navigate('/vision')}
                className={`w-100 mt-2 ${visionCompleted ? 'btn-brand-completed' : 'btn-brand-optional'}`}
                disabled={!businessBasicsCompleted}
              >
                {visionCompleted ? 'View/Edit' : businessBasicsCompleted ? 'Create Vision' : 'Complete Business Basics First'}
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4} className="mb-3">
          <Card className={customerJourneyCompleted ? 'border-success' : ''}>
            <Card.Body>
              <Card.Title>Step 4: Customer Journey & Personas</Card.Title>
              <Card.Text>
                Define customer personas and map their journey through 4 awareness stages.
              </Card.Text>
              {customerJourneyCompleted && (
                <Alert className="mb-2 py-1 small alert-brand-completed">
                  Completed ✓
                </Alert>
              )}
              <Button
                onClick={() => navigate('/customer-journey')}
                className={`w-100 mt-2 ${customerJourneyCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                disabled={!targetMarketCompleted}
              >
                {customerJourneyCompleted ? 'View/Edit' : targetMarketCompleted ? 'Get Started' : 'Complete Target Market First'}
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4} className="mb-3">
          <Card className={resultsAlignmentCompleted ? 'border-success' : ''}>
            <Card.Body>
              <Card.Title>Step 5: Results & Outcomes</Card.Title>
              <Card.Text>
                Select the specific, measurable results your customers achieve from AI-generated options.
              </Card.Text>
              {resultsAlignmentCompleted && (
                <Alert className="mb-2 py-1 small alert-brand-completed">
                  Completed ✓
                </Alert>
              )}
              <Button
                onClick={() => navigate('/results-alignment')}
                className={`w-100 mt-2 ${resultsAlignmentCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                disabled={!customerJourneyCompleted}
              >
                {resultsAlignmentCompleted ? 'View/Edit' : customerJourneyCompleted ? 'Get Started' : 'Complete Customer Journey First'}
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4} className="mb-3">
          <Card className={capacityAssessmentCompleted ? 'border-success' : ''}>
            <Card.Body>
              <Card.Title>Step 6: Capacity Assessment</Card.Title>
              <Card.Text>
                Assess your current capacity and scaling constraints to inform realistic growth targets.
              </Card.Text>
              {capacityAssessmentCompleted && (
                <Alert className="mb-2 py-1 small alert-brand-completed">
                  Completed ✓
                </Alert>
              )}
              <Button
                onClick={() => navigate('/capacity-assessment')}
                className={`w-100 mt-2 ${capacityAssessmentCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                disabled={!resultsAlignmentCompleted}
              >
                {capacityAssessmentCompleted ? 'View/Edit' : resultsAlignmentCompleted ? 'Get Started' : 'Complete Results & Alignment First'}
              </Button>
            </Card.Body>
          </Card>
        </Col>

      </Row>

          {/* View Foundation Summary */}
          <Row className="mt-4">
            <Col>
              <Card className="shadow-sm" style={{ borderLeft: `4px solid ${colors.goldMid}` }}>
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h4 className="mb-3">📋 Your Complete Foundation</h4>
                      <p className="text-muted mb-3">
                        Review your complete Module 1 foundation including business basics, core values, mission, vision, target market, customer journey, results alignment, and capacity assessment.
                      </p>
                      <p className="mb-0">
                        <strong>Status:</strong>{' '}
                        {businessBasicsCompleted && coreValuesCompleted && missionCompleted &&
                         visionCompleted && targetMarketCompleted && customerJourneyCompleted &&
                         resultsAlignmentCompleted && capacityAssessmentCompleted ? (
                          <span className="text-success">✓ Module Complete</span>
                        ) : (
                          <span className="text-warning">In Progress</span>
                        )}
                      </p>
                    </div>
                    <Button
                      size="lg"
                      onClick={() => navigate('/view-foundation')}
                      className="ms-3 btn-brand-primary"
                    >
                      View Foundation Summary →
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* Business Model Module */}
      {activeModule === 'business-model' && (
        <>
          <Row className="mb-3">
            <Col>
              <h3>Business Model Module</h3>
              <p className="text-muted">
                Define your pricing, revenue streams, and business model structure.
              </p>
            </Col>
          </Row>
          <Row>
            {/* Core Product & Pricing */}
            <Col md={6} lg={4} className="mb-4">
              <Card className={`h-100 ${coreProductPricingCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Step 1: Core Product & Pricing</Card.Title>
                  <Card.Text>
                    Define your core product and establish your pricing model.
                  </Card.Text>
                  {coreProductPricingCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/core-product-pricing')}
                    className={`w-100 mt-2 ${coreProductPricingCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                    disabled={!foundationModuleComplete}
                  >
                    {coreProductPricingCompleted ? 'View/Edit' : foundationModuleComplete ? 'Get Started' : 'Complete Foundation Module First'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            {/* Referral Source */}
            <Col md={6} lg={4} className="mb-4">
              <Card className={`h-100 ${referralSourceCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Step 2: Customer Acquisition Mix</Card.Title>
                  <Card.Text>
                    Understand how customers find you to tailor your strategy.
                  </Card.Text>
                  {referralSourceCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/referral-source')}
                    className={`w-100 mt-2 ${referralSourceCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                    disabled={!coreProductPricingCompleted}
                  >
                    {referralSourceCompleted ? 'View/Edit' : coreProductPricingCompleted ? 'Get Started' : 'Complete Core Product & Pricing First'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            {/* Customer Experience */}
            <Col md={6} lg={4} className="mb-4">
              <Card className={`h-100 ${customerExperienceCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Step 3: Customer Experience Design</Card.Title>
                  <Card.Text>
                    Map the customer journey from purchase through ongoing use.
                  </Card.Text>
                  {customerExperienceCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/customer-experience')}
                    className={`w-100 mt-2 ${customerExperienceCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                    disabled={!referralSourceCompleted}
                  >
                    {customerExperienceCompleted ? 'View/Edit' : referralSourceCompleted ? 'Get Started' : 'Complete Customer Acquisition Mix First'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            {/* Competitive Analysis */}
            <Col md={6} lg={4} className="mb-4">
              <Card className={`h-100 ${competitiveAnalysisCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Step 4: Competitive Intelligence</Card.Title>
                  <Card.Text>
                    Analyze competitors and identify market opportunities.
                  </Card.Text>
                  {competitiveAnalysisCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/competitive-analysis')}
                    className={`w-100 mt-2 ${competitiveAnalysisCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                    disabled={!customerExperienceCompleted}
                  >
                    {competitiveAnalysisCompleted ? 'View/Edit' : customerExperienceCompleted ? 'Get Started' : 'Complete Customer Experience First'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            {/* Features & Benefits List */}
            <Col md={6} lg={4} className="mb-4">
              <Card className={`h-100 ${featuresListCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Step 5: Features & Benefits</Card.Title>
                  <Card.Text>
                    Complete list of your features and benefits from your site and competitors.
                  </Card.Text>
                  {featuresListCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/features-benefits')}
                    className={`w-100 mt-2 ${featuresListCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                    disabled={!featuresListCompleted}
                  >
                    {featuresListCompleted ? 'View/Edit' : 'Complete Competitive Intelligence First'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            {/* Product Roadmap */}
            <Col md={6} lg={4} className="mb-4">
              <Card className={`h-100 ${roadmapCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Step 6: Product Roadmap</Card.Title>
                  <Card.Text>
                    Your product development roadmap with priorities and timelines.
                  </Card.Text>
                  {roadmapCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/product-roadmap')}
                    className={`w-100 mt-2 ${roadmapCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                    disabled={!roadmapCompleted}
                  >
                    {roadmapCompleted ? 'View/Edit' : 'Complete Competitive Intelligence First'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            {/* Product Catalog */}
            <Col md={6} lg={4} className="mb-4">
              <Card className={`h-100 ${productCatalogCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Step 7: Product Catalog & Value Ladder</Card.Title>
                  <Card.Text>
                    Map your complete product portfolio and strategic pricing structure.
                  </Card.Text>
                  {productCatalogCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/product-catalog')}
                    className={`w-100 mt-2 ${productCatalogCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                  >
                    {productCatalogCompleted ? 'View/Edit' : 'Get Started'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            {/* Customer Journey Mapping */}
            <Col md={6} lg={4} className="mb-4">
              <Card className={`h-100 ${upgradePathsCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Step 8: Customer Journey Mapping</Card.Title>
                  <Card.Text>
                    Define how customers move through your product ladder with upgrade paths and conversion rates.
                  </Card.Text>
                  {upgradePathsCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/upgrade-paths')}
                    className={`w-100 mt-2 ${upgradePathsCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                    disabled={!productCatalogCompleted}
                  >
                    {upgradePathsCompleted ? 'View/Edit' : productCatalogCompleted ? 'Get Started' : 'Complete Product Catalog First'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            {/* USP Creation */}
            <Col md={6} lg={4} className="mb-4">
              <Card className={`h-100 ${uspCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Step 9: Unique Selling Proposition</Card.Title>
                  <Card.Text>
                    Define what makes your business uniquely valuable to customers.
                  </Card.Text>
                  {uspCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/usp-creation')}
                    className={`w-100 mt-2 ${uspCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                  >
                    {uspCompleted ? 'View/Edit' : 'Get Started'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            {/* Irresistible Offer */}
            <Col md={6} lg={4} className="mb-4">
              <Card className={`h-100 ${irresistibleOfferCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Step 10: Irresistible Offer</Card.Title>
                  <Card.Text>
                    Create a complete offer with bonuses, guarantee, urgency, and risk reversal.
                  </Card.Text>
                  {irresistibleOfferCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/irresistible-offer')}
                    className={`w-100 mt-2 ${irresistibleOfferCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                  >
                    {irresistibleOfferCompleted ? 'View/Edit' : 'Get Started'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* View Business Model Summary */}
          <Row className="mt-4">
            <Col>
              <Card className="shadow-sm" style={{ borderLeft: `4px solid ${colors.goldMid}` }}>
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h4 className="mb-3">Your Complete Business Model</h4>
                      <p className="text-muted mb-3">
                        Review your complete Module 2 business model including core product pricing, customer experience, competitive intelligence, features & benefits, product roadmap, product catalog, USP, and irresistible offer.
                      </p>
                      <p className="mb-0">
                        <strong>Status:</strong>{' '}
                        {coreProductPricingCompleted && customerExperienceCompleted &&
                         competitiveAnalysisCompleted && featuresListCompleted && roadmapCompleted &&
                         productCatalogCompleted && upgradePathsCompleted && uspCompleted && irresistibleOfferCompleted ? (
                          <span className="text-success">✓ Module Complete</span>
                        ) : (
                          <span className="text-warning">In Progress</span>
                        )}
                      </p>
                    </div>
                    <Button
                      size="lg"
                      onClick={() => navigate('/view-business-model')}
                      className="ms-3 btn-brand-primary"
                    >
                      View Business Model Summary →
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* Branding Module */}
      {activeModule === 'branding' && (
        <>
          <Row className="mb-3">
            <Col>
              <h3>Brand Identity Module</h3>
              <p className="text-muted">
                Create your brand identity based on archetype psychology, with custom color palettes, typography, and visual elements.
              </p>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={6} lg={4}>
              <Card className={`h-100 ${brandArchetypeCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Step 1: Brand Archetype Quiz</Card.Title>
                  <Card.Text>
                    Discover your brand archetype through 6 psychology-based questions.
                  </Card.Text>
                  {brandArchetypeCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/dashboard/brand-quiz-intro')}
                    className={`w-100 mt-2 ${brandArchetypeCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                  >
                    {brandArchetypeCompleted ? 'View/Edit' : 'Get Started'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} lg={4}>
              <Card className={`h-100 ${colorPaletteCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Step 2: Color Palette</Card.Title>
                  <Card.Text>
                    AI-generated color palettes based on your archetype. Customize individual colors until perfect.
                  </Card.Text>
                  {colorPaletteCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/dashboard/color-palettes')}
                    className={`w-100 mt-2 ${colorPaletteCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                  >
                    {colorPaletteCompleted ? 'View/Edit' : 'Get Started'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} lg={4}>
              <Card className={`h-100 ${typographyCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Step 3: Typography</Card.Title>
                  <Card.Text>
                    Professional font pairing recommendations (headings + body text) that match your archetype.
                  </Card.Text>
                  {typographyCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/dashboard/typography')}
                    className={`w-100 mt-2 ${typographyCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                  >
                    {typographyCompleted ? 'View/Edit' : 'Get Started'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={6} lg={4}>
              <Card className={`h-100 ${logoDirectionCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Step 4: Logo Direction</Card.Title>
                  <Card.Text>
                    Logo style recommendations including shape language, imagery direction, and design principles.
                  </Card.Text>
                  {logoDirectionCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/dashboard/logo-direction')}
                    className={`w-100 mt-2 ${logoDirectionCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                  >
                    {logoDirectionCompleted ? 'View/Edit' : 'Get Started'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} lg={4}>
              <Card className={`h-100 ${iconographyCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Step 5: Brand Marker</Card.Title>
                  <Card.Text>
                    Upload the icon or symbol that appears on all your generated images for instant brand recognition.
                  </Card.Text>
                  {iconographyCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/dashboard/iconography')}
                    className={`w-100 mt-2 ${iconographyCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                  >
                    {iconographyCompleted ? 'View/Edit' : 'Get Started'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} lg={4}>
              <Card className={`h-100 ${brandStyleCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Step 6: Brand Style</Card.Title>
                  <Card.Text>
                    Choose words that describe your brand's overall style (1-3 words).
                  </Card.Text>
                  {brandStyleCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/brand-style')}
                    className={`w-100 mt-2 ${brandStyleCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                  >
                    {brandStyleCompleted ? 'View/Edit' : 'Get Started'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={6} lg={4}>
              <Card className={`h-100 ${brandTextureCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Step 7: Brand Texture</Card.Title>
                  <Card.Text>
                    Select textures that align with your brand aesthetic (1-3 words).
                  </Card.Text>
                  {brandTextureCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/brand-texture')}
                    className={`w-100 mt-2 ${brandTextureCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                  >
                    {brandTextureCompleted ? 'View/Edit' : 'Get Started'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} lg={4}>
              <Card className={`h-100 ${brandVoiceAnalysisCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Step 8: Brand Voice Analysis</Card.Title>
                  <Card.Text>
                    Analyze your writing samples to create a comprehensive voice description for AI content generation.
                  </Card.Text>
                  {brandVoiceAnalysisCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/brand-voice-analysis')}
                    className={`w-100 mt-2 ${brandVoiceAnalysisCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                  >
                    {brandVoiceAnalysisCompleted ? 'View/Edit' : 'Get Started'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} lg={4}>
              <Card className={`h-100 ${moodBoardCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Step 9: Visual Mood Board</Card.Title>
                  <Card.Text>
                    Curated mood board with visual references that capture your brand aesthetic, energy, and personality.
                  </Card.Text>
                  {moodBoardCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/dashboard/mood-board')}
                    className={`w-100 mt-2 ${moodBoardCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                  >
                    {moodBoardCompleted ? 'View/Edit' : 'Get Started'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Brand Identity Summary Card */}
          <Row className="mt-4">
            <Col>
              <Card className="shadow-sm" style={{ borderLeft: `4px solid ${colors.goldMid}` }}>
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h4 className="mb-3">📋 Your Complete Brand Identity</h4>
                      <p className="text-muted mb-3">
                        Review and download your complete brand identity guide including archetype, colors, typography, logo direction, brand marker, style, texture, voice, and mood board.
                      </p>
                      <p className="mb-0">
                        <strong>Status:</strong>{' '}
                        {brandArchetypeCompleted && colorPaletteCompleted && typographyCompleted &&
                         logoDirectionCompleted && iconographyCompleted && brandStyleCompleted &&
                         brandTextureCompleted && brandVoiceAnalysisCompleted && moodBoardCompleted ? (
                          <span className="text-success">✓ Module Complete</span>
                        ) : (
                          <span className="text-warning">In Progress</span>
                        )}
                      </p>
                    </div>
                    <Button
                      size="lg"
                      onClick={() => navigate('/view-brand-identity')}
                      className="ms-3 btn-brand-primary"
                    >
                      View Complete Brand Guide →
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* Marketing Strategy Module */}
      {activeModule === 'marketing-strategy' && (
        <>
          <Row className="mb-3">
            <Col>
              <h3>Growth & Marketing Strategy</h3>
              <p className="text-muted">
                Build a comprehensive marketing strategy to grow your business systematically.
              </p>
            </Col>
          </Row>

          {/* Marketing Strategy Cards - most are standalone forms */}
          {(() => {
            // Most marketing cards don't need prerequisites - they're standalone forms
            // Only Planned Promotions needs products (has its own check inside the page)
            // Exit Strategy can work with just products (quick-start users)
            const hasProductData = productCatalogCompleted || upgradePathsCompleted;

            return (
              <>

                {/* Step 1: Marketing Assessment */}
                <Row className="mb-4">
                  <Col md={6} lg={4}>
                    <Card className={`h-100 ${marketingAssessmentCompleted ? 'border-success' : ''}`}>
                      <Card.Body>
                        <Card.Title>Step 1: Marketing Assessment</Card.Title>
                        <Card.Text>
                          Assess your current marketing assets, past successes, and biggest challenges.
                        </Card.Text>
                        {marketingAssessmentCompleted && (
                          <Alert className="mb-2 py-1 small alert-brand-completed">
                            Completed ✓
                          </Alert>
                        )}
                        <Button
                          onClick={() => navigate('/marketing-assessment')}
                          className={`w-100 mt-2 ${marketingAssessmentCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                        >
                          {marketingAssessmentCompleted ? 'Review Assessment' : 'Start Assessment →'}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* Step 2: Priorities & Resources */}
                  <Col md={6} lg={4}>
                    <Card className={`h-100 ${marketingPrioritiesCompleted ? 'border-success' : ''}`}>
                      <Card.Body>
                        <Card.Title>Step 2: Priorities & Resources</Card.Title>
                        <Card.Text>
                          Set your budget, timeline, and funnel priorities.
                        </Card.Text>
                        {marketingPrioritiesCompleted && (
                          <Alert className="mb-2 py-1 small alert-brand-completed">
                            Completed ✓
                          </Alert>
                        )}
                        <Button
                          onClick={() => navigate('/marketing-priorities')}
                          className={`w-100 mt-2 ${marketingPrioritiesCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                        >
                          {marketingPrioritiesCompleted ? 'Review Priorities' : 'Set Priorities →'}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={6} lg={4}>
                    <Card className={`h-100 ${clientRetentionCompleted ? 'border-success' : ''}`}>
                      <Card.Body>
                        <Card.Title>Step 3: Client Retention Strategy</Card.Title>
                        <Card.Text>
                          Build systems to keep customers coming back.
                        </Card.Text>
                        {clientRetentionCompleted && (
                          <Alert className="mb-2 py-1 small alert-brand-completed">
                            Completed ✓
                          </Alert>
                        )}
                        <Button
                          onClick={() => navigate('/client-retention')}
                          className={`w-100 mt-2 ${clientRetentionCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                        >
                          {clientRetentionCompleted ? 'Review Strategy' : 'Build Strategy →'}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row className="mb-4">
                  <Col md={6} lg={4}>
                    <Card className={`h-100 ${viralReferralCompleted ? 'border-success' : ''}`}>
                      <Card.Body>
                        <Card.Title>Step 4: Viral & Referral Strategy</Card.Title>
                        <Card.Text>
                          Create systems for word-of-mouth growth.
                        </Card.Text>
                        {viralReferralCompleted && (
                          <Alert className="mb-2 py-1 small alert-brand-completed">
                            Completed ✓
                          </Alert>
                        )}
                        <Button
                          onClick={() => navigate('/viral-referral')}
                          className={`w-100 mt-2 ${viralReferralCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                        >
                          {viralReferralCompleted ? 'Review Strategy' : 'Build Strategy →'}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* Step 5: Planned Promotions */}
                  <Col md={6} lg={4}>
                    <Card className={`h-100 ${plannedPromotionsCompleted ? 'border-success' : ''}`}>
                      <Card.Body>
                        <Card.Title>Step 5: Planned Promotions</Card.Title>
                        <Card.Text>
                          Schedule promotional campaigns with estimated revenue impact.
                        </Card.Text>
                        {plannedPromotionsCompleted && (
                          <Alert className="mb-2 py-1 small alert-brand-completed">
                            Completed ✓
                          </Alert>
                        )}
                        <Button
                          onClick={() => navigate('/planned-promotions')}
                          className={`w-100 mt-2 ${plannedPromotionsCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                        >
                          {plannedPromotionsCompleted ? 'View Promotions' : 'Plan Promotions →'}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Exit Strategy Report - Full Width Card */}
                <Row className="mt-4">
                  <Col>
                    <Card className="shadow-sm" style={{ borderLeft: `4px solid ${colors.goldMid}` }}>
                      <Card.Body className="p-4">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h4 className="mb-3">Step 6: Exit Strategy Report</h4>
                            <p className="text-muted mb-3">
                              Comprehensive analysis of how your marketing strategy impacts business valuation, exit readiness, and strategic gaps. Includes AI-powered recommendations for growth, infrastructure, and milestones.
                            </p>
                            <p className="mb-0">
                              <strong>Status:</strong>{' '}
                              {exitPathConfigured ? (
                                <span className="text-success">✓ Configured</span>
                              ) : hasProductData ? (
                                <span className="text-info">Ready to Configure</span>
                              ) : (
                                <span className="text-warning">Add Products First</span>
                              )}
                            </p>
                          </div>
                          <Button
                            size="lg"
                            onClick={() => navigate(exitPathConfigured ? '/updated-exit-strategy' : '/exit-strategy')}
                            className="ms-3 btn-brand-primary"
                            disabled={!exitPathConfigured && !hasProductData}
                          >
                            {exitPathConfigured ? 'View Exit Strategy Report →' : 'Configure Exit Strategy →'}
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </>
            );
          })()}
        </>
      )}

      {/* Assets Creation Module */}
      {activeModule === 'assets-creation' && (
        <>
          <Row className="mb-3">
            <Col>
              <h3>Assets Creation (Module 5)</h3>
              <p className="text-muted">
                Create professional marketing assets to grow your business systematically.
              </p>
            </Col>
          </Row>
          <Row className="mb-4">
            {/* Lead Magnet */}
            <Col md={6} lg={4}>
              <Card className={`h-100 ${leadMagnetCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Lead Magnet Strategy</Card.Title>
                  <Card.Text>
                    Create a compelling lead magnet with topic, content outline, and landing page copy.
                  </Card.Text>
                  {leadMagnetCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/lead-magnet-topic')}
                    className={`w-100 mt-2 ${leadMagnetCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                  >
                    {leadMagnetCompleted ? 'View/Edit' : 'Get Started'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            {/* Sales Page */}
            <Col md={6} lg={4}>
              <Card className={`h-100 ${salesPageCompleted ? 'border-success' : ''}`}>
                <Card.Body>
                  <Card.Title>Sales Page</Card.Title>
                  <Card.Text>
                    Generate a complete long-form sales page for your core product.
                  </Card.Text>
                  {salesPageCompleted && (
                    <Alert className="mb-2 py-1 small alert-brand-completed">
                      Completed ✓
                    </Alert>
                  )}
                  <Button
                    onClick={() => navigate('/sales-page')}
                    className={`w-100 mt-2 ${salesPageCompleted ? 'btn-brand-completed' : 'btn-brand-primary'}`}
                  >
                    {salesPageCompleted ? 'View/Edit' : 'Get Started'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            {/* Email Sequences */}
            <Col md={6} lg={4}>
              <Card className="h-100">
                <Card.Body>
                  <Card.Title>Email Sequences</Card.Title>
                  <Card.Text>
                    Create automated email sequences for welcome and post-purchase journeys.
                  </Card.Text>
                  <Button
                    onClick={() => navigate('/email-sequences')}
                    className="w-100 mt-2 btn-brand-primary"
                  >
                    Get Started
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>

        </>
      )}

      {/* Battle Plan Module */}
      {activeModule === 'battle-plan' && (
        <>
          <Row className="mb-3">
            <Col>
              <h3>Strategic Intelligence</h3>
              <p className="text-muted">
                Comprehensive reports and strategic analysis across your entire Profit Map.
              </p>
            </Col>
          </Row>
          <Row className="g-4 mb-4">
            <Col md={6} lg={4}>
              <Card className="h-100 shadow-sm" style={{ borderLeft: `4px solid ${colors.goldMid}` }}>
                <Card.Body>
                  <h5 className="mb-3">Foundation Summary</h5>
                  <p className="text-muted mb-3">
                    Your complete Module 1 foundation including business basics, core values,
                    mission, vision, target market, customer journey, results alignment, and capacity assessment.
                  </p>
                  <Button
                    onClick={() => navigate('/view-foundation')}
                    className="btn-brand-primary"
                  >
                    View Foundation Summary →
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="h-100 shadow-sm" style={{ borderLeft: `4px solid ${colors.goldMid}` }}>
                <Card.Body>
                  <h5 className="mb-3">Business Model Summary</h5>
                  <p className="text-muted mb-3">
                    Your complete Module 2 business model including core product pricing, customer experience,
                    competitive intelligence, features & benefits, product catalog, USP, and irresistible offer.
                  </p>
                  <Button
                    onClick={() => navigate('/view-business-model')}
                    className="btn-brand-primary"
                  >
                    View Business Model Summary →
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="h-100 shadow-sm" style={{ borderLeft: `4px solid ${colors.goldMid}` }}>
                <Card.Body>
                  <h5 className="mb-3">🎯 Exit Strategy</h5>
                  <p className="text-muted mb-3">
                    Your timeline and planning for a successful business exit, including
                    milestones, valuation targets, and key dates.
                  </p>
                  <Button
                    onClick={() => navigate('/exit-strategy')}
                    className="btn-brand-primary"
                  >
                    View Exit Strategy →
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="h-100 shadow-sm" style={{ borderLeft: `4px solid ${colors.goldMid}` }}>
                <Card.Body>
                  <h5 className="mb-3">Marketing Strategy & Exit Valuation</h5>
                  <p className="text-muted mb-3">
                    Comprehensive revenue projections, marketing strategy analysis,
                    and updated exit valuation based on your business model.
                  </p>
                  <Button
                    onClick={() => navigate('/updated-exit-strategy')}
                    className="btn-brand-primary"
                  >
                    View Report →
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="h-100 shadow-sm" style={{ borderLeft: `4px solid ${colors.goldMid}` }}>
                <Card.Body>
                  <h5 className="mb-3">📅 Your 90-Day Marketing Battle Plan</h5>
                  <p className="text-muted mb-3">
                    Get week-by-week action items, content output goals, funnel stage breakdowns,
                    and cost comparisons. Your personalized roadmap to marketing success.
                  </p>
                  <Button
                    onClick={() => navigate('/battle-plan')}
                    className="btn-brand-primary"
                  >
                    View Battle Plan →
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* Command Center Module */}
      {activeModule === 'marketing-campaigns' && (
        <>
          <Row className="mb-3">
            <Col>
              <h3>Command Center</h3>
              <p className="text-muted">
                Create, manage, and track all your marketing campaigns and content from one central hub.
              </p>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Alert variant="info">
                <strong>Coming Soon!</strong> Command Center features will be available here.
                {user?.is_admin && (
                  <div className="mt-2">
                    <small className="text-muted">(Admin View: This module is in development)</small>
                  </div>
                )}
              </Alert>
            </Col>
          </Row>
        </>
      )}
      </Container>
    </>
  );
}

export default Dashboard;
